<template>
  <div class="mo-multiselect">
    <!--Unselected panel-->

    <div class="mo-multiselect-pane unselected">
      <Loading :loading="loading" />
      <div class="mo-multiselect-pane-header">
        <strong>{{ title }}</strong>
        <el-input
          v-model="query"
          type="search"
          :placeholder="`Search ${subject}s`"
          prefix-icon="el-icon-search"
          size="small"
        />
      </div>

      <!--Tags-->
      <ul class="mo-multiselect-groups" v-if="grouped">
        <li
          :class="[
            'mo-multiselect-group',
            { closed: !openGroups[group.name] && query.length === 0 },
          ]"
          v-for="group in filteredItems"
          :key="group.name"
        >
          <div class="mo-multiselect-group-title" @click="toggleGroup(group.name)">
            {{ group.name }}
          </div>

          <ul class="mo-multiselect-items" v-if="openGroups[group.name] || query.length > 0">
            <li v-for="item in group.values" :key="getItemValue(item)">
              <el-checkbox
                class="mo-multiselect-item"
                :value="isItemSelected(value, group, item)"
                @change="toggleItem(group, item, $event)"
              >
                {{ getItemLabel(item) }}
              </el-checkbox>
            </li>
          </ul>
        </li>
      </ul>

      <!--Accounts-->
      <ul v-else class="mo-multiselect-items overflow-auto">
        <li v-for="item in filteredItems" :key="getItemValue(item)">
          <el-checkbox
            class="mo-multiselect-item"
            :value="isItemSelected(value, null, item)"
            @change="toggleItem(null, item, $event)"
          >
            {{ getItemLabel(item) }}
          </el-checkbox>
        </li>
      </ul>
    </div>

    <!--Selected panel-->
    <div class="mo-multiselect-pane">
      <Loading :loading="loading" />
      <div class="mo-multiselect-pane-header">
        <span>
          <strong>{{ numSelectedItems }}</strong>
          {{ subject }}s selected
        </span>
      </div>
      <div class="mo-multiselect-empty" v-if="value.length === 0">
        Please select {{ subject }}s on the left panel.
      </div>

      <ul class="mo-multiselect-groups" v-if="grouped">
        <li class="mo-multiselect-group" v-for="group in value" :key="group.name">
          <div class="mo-multiselect-group-title static">{{ group.name }}</div>
          <ul class="mo-multiselect-items">
            <li v-for="item in group.values" :key="getItemValue(item)">
              <el-checkbox
                class="mo-multiselect-item"
                :value="isItemSelected(value, group, item)"
                @change="toggleItem(group, item, $event)"
                >{{ getItemLabel(item) }}</el-checkbox
              >
            </li>
          </ul>
        </li>
      </ul>

      <ul v-else class="mo-multiselect-items overflow-auto">
        <li v-for="item in value" :key="getItemValue(item)">
          <el-checkbox
            class="mo-multiselect-item"
            :value="isItemSelected(value, null, item)"
            @change="toggleItem(null, item, $event)"
            >{{ getItemLabel(item) }}</el-checkbox
          >
        </li>
      </ul>
    </div>
  </div>
</template>
<script>
import Vue from 'vue';
import Loading from './uikit/Loading';

export default {
  name: 'MultiSelect',
  components: { Loading },
  props: {
    subject: { type: String, default: 'account' },
    grouped: { type: Boolean, default: false },
    title: { type: String, required: true },
    items: { type: Array, required: true },
    value: { type: Array },
    labelField: { type: String },
    valueField: { type: String },
    loading: { type: Boolean },
  },
  data() {
    return {
      query: '',
      openGroups: {},
    };
  },
  watch: {
    items: {
      handler: 'onItemsChanged',
      immediate: true,
    },
  },
  computed: {
    numSelectedItems() {
      if (this.grouped) {
        return this.value.reduce((sum, grp) => sum + grp.values.length, 0);
      }

      return this.value.length;
    },

    filteredItems() {
      if (this.query === '') {
        return this.items;
      }

      const regxp = new RegExp(this.query);
      if (!this.grouped) {
        return this.items.filter(item => regxp.test(this.getItemLabel(item)));
      }

      return this.items
        .map(grp => {
          return { ...grp, values: grp.values.filter(item => regxp.test(this.getItemLabel(item))) };
        })
        .filter(grp => grp.values.length > 0);
    },

    groupedValues() {
      const val = this.value.reduce((obj, grp) => ({ ...obj, [grp.name]: grp }), {});
      return this.values.reduce((obj, grp) => {
        if (val[grp.name]) {
          obj[grp.name] = val[grp.name];
          return obj;
        }

        obj[grp.name] = { ...grp, items: [] };
        return obj;
      }, {});
    },
  },
  methods: {
    onItemsChanged(newItems, prevItems) {
      if (!prevItems) return false;
      if (prevItems.length === 0) {
        Vue.set(this.openGroups, newItems[0].name, true);
      }
    },

    toggleGroup(group) {
      Vue.set(this.openGroups, group, !this.openGroups[group]);
    },

    getItemLabel(item) {
      return this.labelField ? item[this.labelField] : item;
    },

    getItemValue(item) {
      return this.valueField ? item[this.valueField] : item;
    },

    isItemSelected(value, group, item) {
      if (group === null) {
        return value.indexOf(item) >= 0;
      }

      const grp = this.value.find(grp => grp.name === group.name);
      if (!grp) {
        return false;
      }

      return grp.values.indexOf(item) >= 0;
    },

    toggleItem(group, item, val) {
      if (group === null) {
        const value = [...this.value];
        if (val) {
          value.push(item);
          this.$emit('input', value);
          return;
        }

        const itemIdx = value.indexOf(item);
        if (itemIdx >= 0) {
          value.splice(itemIdx, 1);
          this.$emit('input', value);
        }
        return;
      }

      const value = [...this.value.map(grp => ({ ...grp, values: [...grp.values] }))];
      const grpVal = value.find(grp => grp.name === group.name);
      if (grpVal) {
        const itemIdx = grpVal.values.indexOf(item);
        if (val && itemIdx < 0) {
          grpVal.values.push(item);
        } else if (itemIdx >= 0) {
          grpVal.values.splice(itemIdx, 1);
          if (grpVal.values.length === 0) {
            value.splice(value.indexOf(grpVal), 1);
          }
        }

        this.$emit('input', value);
      } else if (val) {
        const grpVal = { ...group, values: [item] };
        value.push(grpVal);
        this.$emit('input', value);
      }
    },
  },
};
</script>
<style lang="scss">
.mo-multiselect {
  display: flex;
  align-content: stretch;
  border: 2px solid #bac4d6;
  border-radius: 4px;
  min-height: 250px;
  max-height: 450px;

  .mo-multiselect-pane {
    position: relative;
    width: 50%;
    padding: 10px 14px 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    &.unselected {
      background: #f2f4f8;
      border-right: 1px solid #bac4d6;
    }

    .mo-multiselect-pane-header {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      height: 30px;
      margin-bottom: 14px;
      flex-shrink: 0;

      strong {
        font-size: 14px;
        color: #50586a;
      }

      span {
        color: #aaa;
      }

      .el-input {
        width: auto;
      }
    }
  }

  .mo-multiselect-groups {
    margin: 0 -14px;
    padding: 0;
    list-style: none;
    overflow: hidden;
    overflow-y: auto;

    .mo-multiselect-group {
      .mo-multiselect-group-title {
        position: relative;
        padding: 5px 14px;
        text-transform: uppercase;
        font-weight: bold;
        font-size: 12px;
        color: #777f91;
      }

      &.closed .mo-multiselect-group-title {
        background: none;
        border-bottom-width: 0;

        &:after {
          content: '\0f078';
        }
      }

      &.closed:last-child .mo-multiselect-group-title {
        border-bottom-width: 1px;
      }
    }
  }

  .mo-multiselect-pane.unselected .mo-multiselect-group-title {
    border-bottom: 1px solid #bac4d6;
    border-top: 1px solid #bac4d6;

    cursor: pointer;

    &:after {
      position: absolute;
      top: 5px;
      right: 14px;
      content: '\0f077';
      font-family: 'Font Awesome 5 Free';
    }
  }

  .mo-multiselect-items {
    padding: 0;
    margin: 0 -14px;
    padding: 5px 14px;
    padding-top: 8px;
    list-style: none;
    .mo-multiselect-item {
      font-weight: normal;
      max-width: 100%;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      margin-bottom: 0;

      .el-checkbox__label {
        max-width: 100%;
        text-overflow: ellipsis;
      }
    }
  }

  .mo-multiselect-group .mo-multiselect-items {
    margin: 0;
    background: #fff;
  }

  .mo-multiselect-empty {
    line-height: 150px;
    text-align: center;
    color: #888;
  }
}

.overflow-auto {
  overflow-y: auto;
}
</style>
